/**
 * Utility functions for URL-based routing and tree ID extraction
 */

import { shortNodeKey, type ParsedTreePath, parseTreePath } from '@skill-trees/shared';

export type { ParsedTreePath };
export { parseTreePath };

const DEFAULT_POST_SIGN_OUT_PUBLIC_FALLBACK = '/about';

/**
 * Builds a canonical view URL for a tree with optional selected node.
 * The node segment is shortNodeKey(nodeId) (8-char base64url); resolution uses resolveNodeByUrlKey.
 */
export function buildTreeViewUrl(
    treeId: string,
    nodeId: string | null | undefined,
    options?: { origin?: string },
): string {
    const origin = options?.origin ?? '';
    const path =
        nodeId != null && nodeId !== ''
            ? `/tree/${treeId}/${encodeURIComponent(shortNodeKey(nodeId))}`
            : `/tree/${treeId}`;
    return `${origin}${path}`;
}

/**
 * Decodes a legacy `/tree/:id#fragment` hash for migration to path-based node URLs.
 * Returns null if there is no fragment, the fragment is empty after decode, or decoding throws (malformed % sequences).
 */
export function parseLegacyTreeViewHashNodeSegment(hash: string): string | null {
    if (!hash || hash.length <= 1) {
        return null;
    }
    const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!fragment) {
        return null;
    }
    try {
        const decoded = decodeURIComponent(fragment);
        return decoded || null;
    } catch {
        return null;
    }
}

/**
 * Extracts the tree ID from the current URL path
 * Expected URL formats: /tree/{treeId}, /tree/{treeId}/{nodeSegment}, /tree/{treeId}/edit
 * @returns The tree ID from the URL, or null if not found
 */
export function getTreeIdFromUrl(): string | null {
    return parseTreePath(window.location.pathname).treeId;
}

/**
 * Checks whether a pathname is a tree edit route.
 */
export function isEditTreePath(pathname: string): boolean {
    return parseTreePath(pathname).isEditRoute;
}

/**
 * Returns the preferred navigation target after sign-out for auth-only routes.
 * Returns null when the current route is already public and no redirect is needed.
 */
export function getPostSignOutNavigationTarget(
    pathname: string,
    options?: { publicFallbackPath?: string },
): string | null {
    const parsedTreePath = parseTreePath(pathname);
    if (parsedTreePath.isEditRoute && parsedTreePath.treeId) {
        return buildTreeViewUrl(parsedTreePath.treeId, null);
    }

    if (pathname === '/trees' || pathname.startsWith('/trees/')) {
        return options?.publicFallbackPath ?? DEFAULT_POST_SIGN_OUT_PUBLIC_FALLBACK;
    }

    return null;
}

/**
 * Redirects to a specific tree URL (no selected node segment)
 * @param treeId The tree ID to redirect to
 */
export function redirectToTree(treeId: string): void {
    const newUrl = `/tree/${treeId}`;
    window.history.replaceState(null, '', newUrl);
}

/**
 * Checks if the current URL is a valid tree URL
 * @returns true if the current URL matches a /tree/:treeId[...] pattern
 */
export function isValidTreeUrl(): boolean {
    return getTreeIdFromUrl() !== null;
}

/**
 * Gets the current tree ID without redirecting
 * @param defaultTreeId The default tree ID to use if URL is invalid (defaults to 'tekken-8')
 * @returns The current tree ID
 */
export function getCurrentTreeId(defaultTreeId: string = 'tekken-8'): string {
    const treeId = getTreeIdFromUrl();
    return treeId || defaultTreeId;
}

/**
 * Handles auth-aware routing - redirects from root only after auth is initialized
 * @param defaultTreeId The default tree ID to redirect to (defaults to 'tekken-8')
 * @returns true if a redirect was performed, false otherwise
 */
export function handleAuthAwareRouting(defaultTreeId: string = 'tekken-8'): boolean {
    if (window.location.pathname === '/' || window.location.pathname === '') {
        redirectToTree(defaultTreeId);
        return true;
    }
    return false;
}
